<script lang="ts">
  import { api, ApiError, type Conversation } from '../lib/api';
  import { boardPath, conversationPath, filesPath, router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';
  import Composer from './Composer.svelte';
  import BoardView from './BoardView.svelte';
  import FilesView from './FilesView.svelte';
  import MessageList from './MessageList.svelte';

  let {
    conversation,
    focus = null,
    tab = 'discussion',
    fileId = null,
    pinnedOpen = false,
    onTogglePinned,
    membersOpen = false,
    onToggleMembers,
  }: {
    conversation: Conversation;
    focus?: string | null;
    tab?: 'discussion' | 'files' | 'board';
    fileId?: string | null;
    pinnedOpen?: boolean;
    onTogglePinned: () => void;
    membersOpen?: boolean;
    onToggleMembers: () => void;
  } = $props();

  let error = $state('');
  let fileCount = $state<number | null>(null);
  let optionsOpen = $state(false);

  async function toggleProject() {
    optionsOpen = false;
    error = '';
    try {
      app.upsertConversation(await api.setProject(conversation.id, !conversation.is_project));
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    }
  }

  // The count on the « Fichiers » tab.
  $effect(() => {
    const id = conversation.id;
    fileCount = null;
    api
      .files(id, { limit: '1' })
      .then((page) => id === conversation.id && (fileCount = page.total))
      .catch(() => {});
  });

  const pinnedCount = $derived(app.pinned[conversation.id]?.length);
  // The count in the header button: loaded with the conversation, then kept up to date.
  $effect(() => {
    const id = conversation.id;
    if (!app.pinned[id]) void app.loadPinned(id).catch(() => {});
  });

  const channel = $derived(conversation.kind === 'public' || conversation.kind === 'private');
  const other = $derived(conversation.members.find((m) => m.id !== app.me?.id));
  const typing = $derived(app.typingNames(conversation.id));
  const canManage = $derived(conversation.role === 'admin' || !!app.me?.is_admin);
  const subtitle = $derived.by(() => {
    const parts: string[] = [];
    if (conversation.is_project) parts.push('Projet');
    if (conversation.topic) parts.push(conversation.topic);
    if (conversation.kind === 'direct' && other) {
      parts.push(app.presence[other.id] === 'online' ? 'En ligne' : app.presence[other.id] === 'away' ? 'Absent' : 'Hors ligne');
    }
    return parts.join(' · ');
  });

  async function unarchive() {
    error = '';
    try {
      app.upsertConversation(await api.updateConversation(conversation.id, { archived: false }));
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    }
  }
</script>

<section class="view" aria-label="Conversation {conversation.title}">
  <header>
    <div class="row">
      <button type="button" class="back" aria-label="Retour aux conversations" onclick={() => router.go('/')}>
        <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>
      {#if conversation.kind === 'direct' && other}
        <Avatar user={other} size={28} presence={app.presence[other.id]} />
      {/if}
      <h1>{channel && !conversation.is_project ? `# ${conversation.title}` : conversation.title}</h1>
      <span class="subtitle">
        {subtitle}{#if subtitle && conversation.kind !== 'direct'}{' · '}{/if}{#if conversation.kind !== 'direct'}<button
            type="button"
            class="members-btn"
            aria-pressed={membersOpen}
            onclick={onToggleMembers}
            title="Voir et ajouter des membres">{conversation.members.length} membre{conversation.members.length > 1 ? 's' : ''}</button
          >{/if}
      </span>
      {#if channel && canManage && !conversation.archived_at}
        <div class="options">
          <button type="button" class="icon" aria-label="Options du canal" aria-expanded={optionsOpen} onclick={() => (optionsOpen = !optionsOpen)}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
          </button>
          {#if optionsOpen}
            <div class="menu" role="menu">
              <button type="button" role="menuitem" onclick={toggleProject}>
                {conversation.is_project ? "Retirer l'option projet" : 'Faire de ce canal un projet (tâches)'}
              </button>
            </div>
          {/if}
        </div>
      {/if}
    </div>
    <div class="row tabs-row">
      <nav class="tabs" aria-label="Vues de la conversation">
        <a
          href={conversationPath(conversation.id)}
          aria-current={tab === 'discussion' ? 'page' : undefined}
          onclick={(e) => {
            e.preventDefault();
            router.go(conversationPath(conversation.id));
          }}>Discussion</a
        >
        {#if conversation.is_project}
          <a
            href={boardPath(conversation.id)}
            aria-current={tab === 'board' ? 'page' : undefined}
            onclick={(e) => {
              e.preventDefault();
              router.go(boardPath(conversation.id));
            }}>Tâches{#if conversation.open_task_count}<span class="count">{conversation.open_task_count}</span>{/if}</a
          >
        {/if}
        <a
          href={filesPath(conversation.id)}
          aria-current={tab === 'files' ? 'page' : undefined}
          onclick={(e) => {
            e.preventDefault();
            router.go(filesPath(conversation.id));
          }}>Fichiers{#if fileCount}<span class="count">{fileCount}</span>{/if}</a
        >
      </nav>
      <button type="button" class="pins" aria-pressed={pinnedOpen} onclick={onTogglePinned}>
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M9 3h6l-1 6 4 4H6l4-4zM12 13v8" /></svg>
        Épinglés{pinnedCount ? ` · ${pinnedCount}` : ''}
      </button>
    </div>
  </header>

  {#if error && !conversation.archived_at}<p class="error header-error" role="alert">{error}</p>{/if}
  {#if tab === 'board'}
    {#key conversation.id}
      <BoardView {conversation} />
    {/key}
  {:else if tab === 'files'}
    {#key conversation.id}
      <FilesView {conversation} {fileId} onTotal={(n) => (fileCount = n)} />
    {/key}
  {:else}
  {#key conversation.id}
    <MessageList conversationId={conversation.id} {focus} archived={!!conversation.archived_at} />
  {/key}

  <p class="typing" aria-live="polite">
    {#if typing.length === 1}{typing[0]} écrit…{:else if typing.length > 1}{typing.join(', ')} écrivent…{/if}
  </p>

  {#if conversation.archived_at}
    <div class="archived">
      <span>Ce canal est archivé : on peut le lire, mais plus y écrire.</span>
      {#if canManage}<button type="button" class="btn" onclick={unarchive}>Désarchiver</button>{/if}
    </div>
    {#if error}<p class="error" role="alert">{error}</p>{/if}
  {:else}
    {#key conversation.id}
      <Composer
        conversationId={conversation.id}
        placeholder={channel ? `Message dans #${conversation.title}` : `Message à ${conversation.title}`}
      />
    {/key}
  {/if}
  {/if}
</section>

<style>
  .view {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: var(--bg);
  }
  header {
    display: flex;
    flex-direction: column;
    padding: 8px 24px 0;
    border-bottom: 1px solid var(--border);
  }
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 40px;
  }
  .tabs-row {
    gap: 4px;
  }
  .options {
    position: relative;
  }
  .icon {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .icon:hover {
    background: var(--bg-hover);
  }
  .menu {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 20;
    min-width: 260px;
    padding: 4px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.16);
  }
  .menu button {
    width: 100%;
    min-height: 36px;
    padding: 0 10px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    text-align: left;
  }
  .menu button:hover {
    background: var(--bg-hover);
  }
  .header-error {
    margin: 8px 24px 0;
  }
  h1 {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    white-space: nowrap;
  }
  .subtitle {
    flex: 1;
    min-width: 0;
    color: var(--muted);
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .members-btn {
    border: 0;
    background: none;
    padding: 0;
    color: inherit;
    font: inherit;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
  }
  .members-btn:hover,
  .members-btn[aria-pressed='true'] {
    color: var(--accent-text);
  }
  .tabs {
    flex: 1;
    display: flex;
    gap: 4px;
  }
  .tabs a {
    height: 40px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 12px;
    color: var(--muted);
    font-weight: 500;
    text-decoration: none;
    border-bottom: 2px solid transparent;
  }
  .tabs a:hover {
    color: var(--ink);
  }
  .tabs a[aria-current='page'] {
    color: var(--ink);
    font-weight: 600;
    border-bottom-color: var(--accent);
  }
  .tabs .count {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-2);
    background: var(--status-todo-soft);
    border-radius: 9px;
    padding: 0 6px;
    line-height: 18px;
  }
  .tabs a[aria-current='page'] .count {
    color: var(--accent-on-soft);
    background: var(--accent-selected);
  }
  .pins {
    flex: 0 0 auto;
    height: 30px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    border: 1px solid var(--border);
    border-radius: 15px;
    background: var(--bg);
    color: var(--ink-2);
    font-size: 13px;
    font-weight: 500;
  }
  .pins[aria-pressed='true'] {
    border-color: var(--accent-border);
    background: var(--accent-soft);
    color: var(--accent-on-soft);
  }
  .back {
    display: none;
    width: 44px;
    height: 44px;
    margin-left: -12px;
    border: 0;
    background: transparent;
    color: var(--accent-text);
    align-items: center;
    justify-content: center;
  }
  .typing {
    margin: 0;
    min-height: 18px;
    padding: 0 36px;
    font-size: 12px;
    color: var(--muted);
  }
  .archived {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin: 8px 24px 16px;
    padding: 12px 16px;
    border-radius: var(--radius-m);
    background: var(--bg-page);
    color: var(--ink-2);
  }
  .archived span {
    flex: 1;
  }
  .error {
    margin: 0 24px 16px;
  }
  @media (max-width: 759px) {
    header {
      padding: 4px 12px 0;
    }
    .row:first-child {
      flex-wrap: wrap;
      row-gap: 0;
    }
    .back {
      display: flex;
    }
    .subtitle {
      flex-basis: 100%;
      padding-left: 44px;
      font-size: 12px;
    }
    .tabs a {
      padding: 0 8px;
    }
  }
</style>
